import type { TFunction } from "i18next";
import { compact, flatMap, isEmpty, split, sumBy } from "lodash";
import { DateTime } from "luxon";
import { documentNumber } from "./document-number";
import { formatPrice } from "./price";
import {
	formatSiret,
	PRICE_WIDTH,
	PRINTED_CURRENCY,
	paymentMethods,
	receiptText,
	receiptTotalLine,
	saleDiscountCents,
	sellerLines,
	vatTable,
} from "./receipt";
import { formatVatRate, lineName, vatBreakdown } from "./sale-lines";

type Translate = TFunction<"translation", undefined>;

interface InvoiceData {
	invoice: Invoice;
	sale: Sale;
	saleProducts: SaleProduct[];
	salePayments: SalePayment[];
	t: Translate;
	columns: number;
}

/**
 * A buyer acting for a business. Their invoice has to carry the late-payment
 * terms of art. L441-9 Code de commerce, which a consumer's does not.
 */
export const isBusinessBuyer = (invoice: Invoice) =>
	Boolean(
		invoice.buyerCompanyName ||
			invoice.buyerRegistrationNumber ||
			invoice.buyerVatNumber,
	);

/** Character widths for the "qty x unit price" / VAT rate / line total columns. */
export const invoiceLineWidths = (columns: number) => {
	const rate = 6;
	const total = Math.max(PRICE_WIDTH, Math.round(columns * 0.25));

	return { detail: Math.max(1, columns - rate - total), rate, total };
};

/**
 * A line of the invoice table. When the detail does not fit its column, it gets
 * a line of its own rather than be cut: a truncated price is a wrong price.
 */
const lineRow = (
	detail: string,
	rate: string,
	total: string,
	columns: number,
): ReceiptLine[] => {
	const width = invoiceLineWidths(columns);
	const fits = detail.length <= width.detail;

	return [
		...(fits ? [] : receiptText(detail, columns)),
		{
			kind: "columns",
			cols: [
				{ text: fits ? detail : "", width: width.detail },
				{ text: rate, width: width.rate, align: "right" },
				{ text: total, width: width.total, align: "right" },
			],
		},
	];
};

// A French company's number is its SIREN, printed as such; another country's
// register number is printed as it was typed, under a generic label.
const registrationLine = (registrationNumber: string, t: Translate) =>
	/^\d{9}$/.test(registrationNumber)
		? t("print.identity.siren", { siren: formatSiret(registrationNumber) })
		: t("print.identity.registrationNumber", { registrationNumber });

const buyerLines = (invoice: Invoice, t: Translate, columns: number) =>
	flatMap(
		compact([
			invoice.buyerCompanyName,
			invoice.buyerName,
			...split(invoice.buyerAddress ?? "", "\n"),
			invoice.buyerRegistrationNumber &&
				registrationLine(invoice.buyerRegistrationNumber, t),
			invoice.buyerVatNumber &&
				t("print.identity.vatNumber", { vatNumber: invoice.buyerVatNumber }),
		]),
		(text) => receiptText(text, columns),
	);

/**
 * The invoice issued for a sale (art. 242 nonies A, annexe II CGI). Who sold and
 * who bought come from the invoice, as copied when it was issued; the amounts
 * come from the VAT each line stored when the sale was recorded.
 */
export const formatDataPrinterInvoice = ({
	invoice,
	sale,
	saleProducts,
	salePayments,
	t,
	columns,
}: InvoiceData): ReceiptLine[] => {
	const price = (cents: number) =>
		formatPrice(cents, sale.totalAmountCurrency, PRINTED_CURRENCY);
	const date = (iso: string) => DateTime.fromISO(iso).toFormat("dd/MM/yyyy");
	const taxFreeAmountCents = sumBy(saleProducts, "taxFreeAmountCents");
	const vatAmountCents = sumBy(saleProducts, "vatAmountCents");
	const discountCents = saleDiscountCents(sale, saleProducts);
	const methods = paymentMethods(salePayments);

	return [
		...sellerLines(
			{
				name: invoice.sellerName,
				legalName: invoice.sellerLegalName,
				legalForm: invoice.sellerLegalForm,
				shareCapitalCents: invoice.sellerShareCapitalCents,
				addressLines: split(invoice.sellerAddress ?? "", "\n"),
				siret: invoice.sellerSiret,
				vatNumber: invoice.sellerVatNumber,
				rcs: invoice.sellerRcs,
			},
			t,
			columns,
		),
		{ kind: "feed", lines: 1 },

		{
			kind: "text",
			text: t("print.invoice.invoiceNumber", {
				invoiceNumber: documentNumber(invoice.number),
			}),
			align: "center",
			bold: true,
		},
		...receiptText(
			t("print.invoice.issuedAt", { date: date(invoice.issuedAt) }),
			columns,
			{ align: "center" },
		),
		// The date of supply: the goods change hands at the till.
		...receiptText(
			t("print.invoice.saleDate", {
				date: date(sale.createdAt),
				saleNumber: documentNumber(sale.number),
			}),
			columns,
			{ align: "center" },
		),
		{ kind: "feed", lines: 1 },

		...receiptText(t("print.invoice.buyer"), columns, { bold: true }),
		...buyerLines(invoice, t, columns),
		{ kind: "feed", lines: 1 },

		...receiptText(t("print.invoice.operation"), columns),
		{ kind: "feed", lines: 1 },

		...lineRow(
			t("print.invoice.lineDetail"),
			t("print.invoice.vatRate"),
			t("print.invoice.lineTotal"),
			columns,
		),
		{ kind: "rule" },
		// Net of the sale's promotion: each line's share of what was charged, as
		// the API split it. The unit price is that share over the quantity.
		...flatMap(saleProducts, (saleProduct) => [
			...receiptText(lineName(saleProduct), columns),
			...lineRow(
				`${saleProduct.quantity} x ${price(
					saleProduct.quantity
						? saleProduct.taxFreeAmountCents / saleProduct.quantity
						: 0,
				)}`,
				formatVatRate(saleProduct.vatRatePercentCents),
				price(saleProduct.taxFreeAmountCents),
				columns,
			),
		]),
		{ kind: "rule" },

		...vatTable(
			vatBreakdown(saleProducts),
			sale.totalAmountCurrency,
			t,
			columns,
		),
		{ kind: "rule" },
		receiptTotalLine(
			t("print.invoice.taxFreeAmount", { amount: price(taxFreeAmountCents) }),
			columns,
		),
		receiptTotalLine(
			t("print.invoice.vatAmount", { amount: price(vatAmountCents) }),
			columns,
		),
		receiptTotalLine(
			t("print.invoice.totalAmount", { amount: price(sale.totalAmountCents) }),
			columns,
		),
		...(discountCents > 0
			? receiptText(
					t("print.invoice.discount", { amount: price(discountCents) }),
					columns,
					{ align: "right" },
				)
			: []),
		{ kind: "feed", lines: 1 },

		...receiptText(
			isEmpty(methods)
				? t("print.invoice.paidOn", { date: date(sale.createdAt) })
				: t("print.invoice.paidOnBy", {
						date: date(sale.createdAt),
						methods,
					}),
			columns,
		),
		...(isBusinessBuyer(invoice)
			? [
					...receiptText(t("print.invoice.latePayment"), columns),
					...receiptText(t("print.invoice.earlyPayment"), columns),
				]
			: []),
		{ kind: "feed", lines: 3 },
		{ kind: "cut" },
	];
};
